import axiosInstance from "@/utils/axiosInstance";
import { API_ENDPOINTS } from "@/constants/config";

/**
 * Vehicle Service - Frontend API client for vehicle and student mapping operations
 */

/**
 * Get all student vehicle mappings (flat list)
 * @returns {Promise<Object>} Response containing flat array of all student mappings
 */
export const getAllStudentMappings = async () => {
  try {
    const response = await axiosInstance.get(API_ENDPOINTS.vehicleRouteMappingsFlat);
    return response.data.data;
  } catch (error) {
    console.error("Error fetching student mappings:", error);
    throw error;
  }
};

/**
 * Get student mappings grouped by vehicle route
 * @returns {Promise<Object>} Response containing route-grouped student mappings
 */
export const getStudentMappingsByRoute = async () => {
  try {
    const response = await axiosInstance.get(API_ENDPOINTS.vehicleRouteMappingsGrouped);
    return response.data.data;
  } catch (error) {
    console.error("Error fetching student mappings by route:", error);
    throw error;
  }
};

/**
 * Get students at a specific stop
 * @param {string} routeId - Vehicle Route ID
 * @param {string} shiftId - Shift ID
 * @param {string} stopId - Stop ID
 * @returns {Promise<Object>} Response containing students at the stop
 */
export const getStudentsAtStop = async (routeId, shiftId, stopId) => {
  try {
    const response = await axiosInstance.get(
      API_ENDPOINTS.vehicleRouteMappingsStop(routeId, shiftId, stopId)
    );
    return response.data.data;
  } catch (error) {
    console.error("Error fetching students at stop:", error);
    throw error;
  }
};

/**
 * Get a student's transport assignment details (pickup and drop)
 * @param {string} studentId - Student ID
 * @returns {Promise<Object>} Response containing student's transport assignments
 */
export const getStudentTransportDetails = async (studentId) => {
  try {
    const response = await axiosInstance.get(
      API_ENDPOINTS.vehicleRouteMappingsStudent(studentId)
    );
    return response.data.data;
  } catch (error) {
    console.error("Error fetching student transport details:", error);
    throw error;
  }
};

/**
 * Get statistics about student vehicle mappings
 * @returns {Promise<Object>} Response containing mapping statistics
 */
export const getStudentMappingStatistics = async () => {
  try {
    const response = await axiosInstance.get(
      API_ENDPOINTS.vehicleRouteMappingsStats
    );
    return response.data.data;
  } catch (error) {
    console.error("Error fetching student mapping statistics:", error);
    throw error;
  }
};

/**
 * Get all vehicle routes with populated student details
 * @returns {Promise<Object>} Response containing all vehicle routes
 */
export const getAllVehicleRoutes = async () => {
  try {
    const response = await axiosInstance.get(API_ENDPOINTS.vehicleRoutes);
    return response.data.data;
  } catch (error) {
    console.error("Error fetching vehicle routes:", error);
    throw error;
  }
};

/**
 * Import student bus route mappings from Excel file
 * @param {File} file - Excel file containing student mappings
 * @returns {Promise<Object>} Response containing import results
 */
export const importStudentBusRouteMappings = async (file) => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await axiosInstance.post(
      API_ENDPOINTS.vehicleRouteMappingsImport,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error importing student bus route mappings:", error);
    throw error;
  }
};

export default {
  getAllStudentMappings,
  getStudentMappingsByRoute,
  getStudentsAtStop,
  getStudentTransportDetails,
  getStudentMappingStatistics,
  getAllVehicleRoutes,
  importStudentBusRouteMappings,
};
